import React, { useEffect, useState } from "react";
import axios from "../../lib/axios.js"; // Adjust to your setup
import { jsPDF } from "jspdf";
import { Button } from "@/components/ui/button.jsx";
import { DateTime } from "luxon";
import { DownloadIcon } from "lucide-react";

// We'll mimic the original "cards" (titles, descriptions, etc.)
// and "shadcn" color palette.

const ShadcnStylePdf = ({ community, dateRange }) => {
    const { id: communityId, community_name: communityName } = community;
    // Example dateRange shape:
    // { from: new Date("2025-01-01"), to: new Date("2025-01-07") }
    const [aggregatedCallData, setAggregatedCallData] = useState({
        total_calls: 0,
        total_incoming_calls: 0,
        total_outgoing_calls: 0,
        total_outgoing_community_answered: 0,
    });
    const [isLoading, setIsLoading] = useState(true);

    // On mount (or on dateRange change), fetch aggregated call data
    useEffect(() => {
        if (!dateRange?.from || !dateRange?.to) return;
        fetchCallData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [communityId, dateRange]);

    const fetchCallData = async () => {
        setIsLoading(true);
        try {
            // Convert dateRange to YYYY-MM-DD strings if needed
            const adjustedDateRange = {
                from: new Date(dateRange.from.setHours(0, 0, 0, 0)), // Start of the day
                to: new Date(dateRange.to.setHours(23, 59, 59, 999)), // End of the day
            };

            const params = {
                start_date: adjustedDateRange.from.toISOString().split("T")[0],
                end_date: adjustedDateRange.to.toISOString().split("T")[0],
                community_id: communityId,
            };
            const encodedParams = new URLSearchParams(params).toString();

            const response = await axios.get(`/dashboard/calls-aggregate?${encodedParams}`);
            setAggregatedCallData(response.data);
        } catch (error) {
            console.error("Error fetching aggregated call data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // "cards" array, like your original Shadcn code
    const communityAnswerRate =
        parseInt(aggregatedCallData.total_outgoing_calls) === 0
            ? 0
            : (aggregatedCallData.total_outgoing_community_answered /
                aggregatedCallData.total_outgoing_calls) *
            100;

    const cards = [
        {
            title: "Total Calls",
            value: aggregatedCallData.total_calls,
            // "Icon" placeholder text; real icons are tricky in PDF unless you embed images
            iconColor: [107, 114, 128], // text-gray-500 => #6b7280
            description:
                "Total calls made either from Flair or received from callbacks.",
        },
        {
            title: "Total Outgoing Calls",
            value: aggregatedCallData.total_outgoing_calls,
            iconColor: [59, 130, 246], // text-blue-500 => #3b82f6
            description: "All calls made from Flair to the community.",
        },
        {
            title: "Total Incoming Calls",
            value: aggregatedCallData.total_incoming_calls,
            iconColor: [16, 185, 129], // text-green-500 => #10b981
            description: "All calls received as callbacks.",
        },
        {
            title: "Total Answered Outgoing Calls",
            value: aggregatedCallData.total_outgoing_community_answered,
            iconColor: [16, 185, 129], // text-green-500 => #10b981
            description: "Outgoing calls that were picked up and pressed 1.",
        },
        {
            title: "Leasing Team Answer Rate",
            value: `${parseFloat(communityAnswerRate).toFixed(2)}%`,
            iconColor: [0, 0, 0],
            description: "Leasing Team Answer Rate: The percentage of outgoing calls that were answered by the community",
        },
    ];

    // Helper to draw a single card on the PDF at (x, y)
    // with width = cardW and height = cardH
    const drawCard = (pdf, x, y, card, cardW, cardH) => {
        // Card background: #f9fafb => [249, 250, 251]
        pdf.setFillColor(249, 250, 251);
        // Rounded corners for "card" look
        pdf.roundedRect(x, y, cardW, cardH, 3, 3, "F");

        // "Icon" in top-right corner
        // We'll just place text, using card.iconColor
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);
        pdf.setTextColor(card.iconColor[0], card.iconColor[1], card.iconColor[2]);

        // Title near top-left
        // text-gray-600 => #4b5563 => [75, 85, 99]
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.setTextColor(75, 85, 99);
        // Because "title" can be somewhat long, we limit width
        pdf.text(card.title, x + 4, y + 10, { maxWidth: cardW - 16 });

        // Big "value" in the middle
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(14);
        // We'll use black for the main value
        pdf.setTextColor(0, 0, 0);
        // Center vertically around y+30
        pdf.text(`${card.value}`, x + 4, y + 28);

        // Description near the bottom
        // text-gray-500 => #6b7280 => [107,114,128]
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7);
        pdf.setTextColor(107, 114, 128);
        // We'll place it near y+40, allowing multiple lines if needed
        pdf.text(card.description, x + 4, y + 40, { maxWidth: cardW - 8 });
    };

    // Generate the PDF
    const generatePdf = () => {
        const pdf = new jsPDF("portrait", "mm", "A4"); // A4: 210mm wide, 297mm tall
        const pageWidth = pdf.internal.pageSize.getWidth();

        const fromLuxon = DateTime.fromJSDate(new Date(dateRange.from));
        const toLuxon = DateTime.fromJSDate(new Date(dateRange.to));

        // 1) A top bar (like Shadcn header)
        pdf.setFillColor(241, 245, 249); // near 'slate-100'
        pdf.rect(0, 0, pageWidth, 20, "F");

        // "Call Analytics" text in the bar
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(14);
        // slate-700 => #374151 => [55,65,81]
        pdf.setTextColor(55, 65, 81);
        pdf.text(`${communityName} ${fromLuxon.toFormat("MM/dd/yy")}-${toLuxon.toFormat("MM/dd/yy")}`, 10, 13);

        // 2) Cards: 5 across
        // We'll do a single row of 5 cards if it fits.
        // Each card ~36mm wide, 50mm high, with 3mm spacing between them
        const marginLeft = 10;
        const marginTop = 30;
        const cardW = 36;
        const cardH = 50;
        const spacing = 3;

        // For 5 cards in one row:
        // total width = 5*36 + 4*3 = 180 + 12 = 192 mm (within 210 - 2*10 margin = 190... slightly tight but we can do it)
        // We'll just place them in a single line for a "grid cols-5" look
        let currentX = marginLeft;
        let currentY = marginTop;
        cards.forEach((card, i) => {
            drawCard(pdf, currentX, currentY, card, cardW, cardH);
            currentX += cardW + spacing;
        });

        // 3) Save the PDF
        pdf.save(`${communityName}_${fromLuxon.toFormat("MM-dd-yy")}--${toLuxon.toFormat("MM-dd-yy")}.pdf`);
    };

    return (
        <Button variant="ghost" onClick={generatePdf} isLoading={isLoading}>
            <DownloadIcon className="h-4 w-4 mr-2"/>
            Export
        </Button>
    )
};

export default ShadcnStylePdf;